/**
 * Session storage + React hook for the landing page.
 *
 * Tokens are kept in `localStorage` for simplicity — vulnerable to XSS, but
 * acceptable for a marketing site that proxies calls to a public Cognito-
 * backed API. If this ever serves sensitive data, migrate to an httpOnly
 * cookie flow driven by a backend session.
 *
 * Cross-tab sync: we listen to the native `storage` event (fired in other
 * tabs) and dispatch a local `minuta-auth-change` event for same-tab
 * updates. `useAuth` subscribes to both so the Header reacts immediately
 * after login/logout.
 */

import { useSyncExternalStore } from "react";
import { refresh as apiRefresh, type Tokens } from "./api";

const STORAGE_KEY = "minuta.session";
const CHANGE_EVENT = "minuta-auth-change";

export interface Session {
  tokens: Tokens;
  email: string;
  firstName?: string;
  lastName?: string;
  // Unix seconds — computed from `expires_in` at save time so we don't have
  // to trust the clock of whichever tab is reading it later.
  expiresAt: number;
}

interface IdTokenClaims {
  email?: string;
  given_name?: string;
  family_name?: string;
  exp?: number;
}

// JWT payload is base64url-encoded JSON between two dots. We only touch it
// to show the email in the menu — trust comes from the API Gateway JWT
// authorizer, not the browser.
function decodeIdToken(idToken: string): IdTokenClaims {
  try {
    const payload = idToken.split(".")[1];
    if (!payload) return {};
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "=",
    );
    const json = atob(padded);
    // atob returns binary — decode UTF-8 so non-ASCII names survive.
    const utf8 = decodeURIComponent(
      Array.from(json)
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );
    return JSON.parse(utf8) as IdTokenClaims;
  } catch {
    return {};
  }
}

function emit() {
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function saveSession(tokens: Tokens): Session {
  const claims = decodeIdToken(tokens.id_token);
  const session: Session = {
    tokens,
    email: claims.email ?? "",
    firstName: claims.given_name,
    lastName: claims.family_name,
    expiresAt: Math.floor(Date.now() / 1000) + tokens.expires_in,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  emit();
  return session;
}

export function loadSession(): Session | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw) as Session;
    // Drop expired sessions eagerly so the UI doesn't flash "logged in"
    // before the first refresh call fails.
    if (session.expiresAt && session.expiresAt * 1000 < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return session;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function clearSession() {
  localStorage.removeItem(STORAGE_KEY);
  emit();
}

/**
 * Exchange the current refresh token for a fresh access + id token set and
 * persist the rotated session. Used after Stripe Checkout so the new
 * `pro_users` group membership lands in the id_token without re-login.
 *
 * Cognito does not rotate the refresh token, so we keep the original one
 * when the response comes back empty on that field. Returns the updated
 * session or `null` when there's no session to refresh.
 */
export async function refreshSession(): Promise<Session | null> {
  const current = loadSession();
  if (!current?.tokens.refresh_token) return null;
  const next = await apiRefresh(current.tokens.refresh_token);
  const tokens: Tokens = {
    ...next,
    // `refresh_token` is absent on successful refresh — preserve the one
    // we already have so future refreshes keep working.
    refresh_token: next.refresh_token || current.tokens.refresh_token,
  };
  return saveSession(tokens);
}

function subscribe(onChange: () => void) {
  const handleStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY || e.key === null) onChange();
  };
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener("storage", handleStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener("storage", handleStorage);
  };
}

// Cache the snapshot so useSyncExternalStore's referential equality check
// only returns a new object when the underlying storage actually changed.
let cachedRaw: string | null = null;
let cachedSession: Session | null = null;

function getSnapshot(): Session | null {
  const raw = typeof localStorage === "undefined" ? null : localStorage.getItem(STORAGE_KEY);
  if (raw === cachedRaw) return cachedSession;
  cachedRaw = raw;
  cachedSession = loadSession();
  return cachedSession;
}

function getServerSnapshot(): Session | null {
  return null;
}

export function useSession(): Session | null {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export function displayName(session: Session): string {
  const full = [session.firstName, session.lastName].filter(Boolean).join(" ").trim();
  return full || session.email || "Conta";
}

export function initials(session: Session): string {
  const first = session.firstName?.[0];
  const last = session.lastName?.[0];
  if (first || last) return `${first ?? ""}${last ?? ""}`.toUpperCase();
  return (session.email?.[0] ?? "?").toUpperCase();
}
